import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowsClockwise,
  CaretDown,
  CaretRight,
  CloudArrowUp,
  DownloadSimple,
  FolderOpen,
  HardDrives,
} from '@phosphor-icons/react';
import { API } from '../config';
import { fileKind, IconFolderClosed, TreeFileGlyph, TreeGuideGutter } from './OrderFilesPanel';
import {
  ensureFolderPermission,
  getAutoSyncPreference,
  isBrowserFolderSyncSupported,
  loadLocalFolderHandle,
  pickLocalNetworkFolder,
  setAutoSyncPreference,
  syncLocalFolderToServer,
} from '../utils/networkFolderLocalSync';

/**
 * Network files pane: browses the server mirror (live share or synced copy).
 * Office staff connect their Z:\ folder once in Chrome/Edge and click Sync — no local scripts.
 */
export default function NetworkFilesPanel({ t }) {
  const [status, setStatus] = useState(null);
  const [childrenByPath, setChildrenByPath] = useState(() => new Map());
  const [loadingPaths, setLoadingPaths] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set());
  const [selected, setSelected] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [localFolderName, setLocalFolderName] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null);
  const [autoSync, setAutoSync] = useState(() => getAutoSyncPreference());
  const localHandleRef = useRef(null);
  const autoSyncRanRef = useRef(false);

  const browserSyncSupported = isBrowserFolderSyncSupported();

  const fetchChildren = useCallback(
    async (path) => {
      setLoadingPaths((prev) => new Set(prev).add(path));
      try {
        const res = await axios.get(`${API}/network-files/browse`, {
          params: { path },
          withCredentials: true,
        });
        setChildrenByPath((prev) => {
          const next = new Map(prev);
          next.set(path, res.data.entries || []);
          return next;
        });
      } catch {
        toast.error(t('filesNetworkLoadFailed'));
        setChildrenByPath((prev) => {
          const next = new Map(prev);
          next.set(path, []);
          return next;
        });
      } finally {
        setLoadingPaths((prev) => {
          const next = new Set(prev);
          next.delete(path);
          return next;
        });
      }
    },
    [t]
  );

  const loadRoot = useCallback(async () => {
    setInitialLoading(true);
    try {
      const res = await axios.get(`${API}/network-files/status`, { withCredentials: true });
      setStatus(res.data);
      if (res.data?.configured && res.data?.available) {
        await fetchChildren('');
      }
    } catch {
      setStatus({ configured: true, available: false, label: null });
    } finally {
      setInitialLoading(false);
    }
  }, [fetchChildren]);

  useEffect(() => {
    void loadRoot();
  }, [loadRoot]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const saved = await loadLocalFolderHandle();
      if (cancelled || !saved?.handle) return;
      localHandleRef.current = saved.handle;
      setLocalFolderName(saved.name || saved.handle.name);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const runSync = useCallback(
    async (handle) => {
      if (!handle || syncing) return;
      const ok = await ensureFolderPermission(handle);
      if (!ok) {
        toast.error(t('filesNetworkPermissionDenied'));
        return;
      }
      setSyncing(true);
      setSyncProgress(null);
      try {
        const result = await syncLocalFolderToServer(handle, (p) => setSyncProgress(p));
        if (result.skipped) {
          toast.info(t('filesNetworkSyncUpToDate'));
        } else {
          toast.success(t('filesNetworkSyncDone').replace('{count}', String(result.uploaded)));
        }
        await loadRoot();
      } catch (err) {
        const msg = err?.response?.data?.detail;
        toast.error(typeof msg === 'string' ? msg : t('filesNetworkSyncFailed'));
      } finally {
        setSyncing(false);
        setSyncProgress(null);
      }
    },
    [syncing, t, loadRoot]
  );

  const chooseFolder = async () => {
    try {
      const handle = await pickLocalNetworkFolder();
      localHandleRef.current = handle;
      setLocalFolderName(handle.name);
      toast.success(t('filesNetworkFolderConnected').replace('{name}', handle.name));
      await runSync(handle);
    } catch (err) {
      if (err?.message === 'UNSUPPORTED_BROWSER') {
        toast.error(t('filesNetworkUnsupportedBrowser'));
        return;
      }
      if (err?.name === 'AbortError') return;
      toast.error(t('filesNetworkConnectFailed'));
    }
  };

  const syncNow = async () => {
    let handle = localHandleRef.current;
    if (!handle) {
      const saved = await loadLocalFolderHandle();
      handle = saved?.handle;
      localHandleRef.current = handle;
    }
    if (!handle) {
      await chooseFolder();
      return;
    }
    await runSync(handle);
  };

  const hasLiveShare = Boolean(status?.available && status?.source === 'live');
  const showBrowserSync = !initialLoading && !hasLiveShare;

  // Auto-sync once per Files-tab visit when a folder was connected before.
  useEffect(() => {
    if (initialLoading || !autoSync || autoSyncRanRef.current || !localFolderName || hasLiveShare) return;
    if (!browserSyncSupported) return;
    autoSyncRanRef.current = true;
    const handle = localHandleRef.current;
    if (handle) void runSync(handle);
  }, [initialLoading, autoSync, localFolderName, hasLiveShare, browserSyncSupported, runSync]);

  const refresh = async () => {
    setChildrenByPath(new Map());
    setExpanded(new Set());
    setSelected(null);
    autoSyncRanRef.current = false;
    await loadRoot();
  };

  const toggleExpand = (path) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else {
        next.add(path);
        if (!childrenByPath.has(path)) void fetchChildren(path);
      }
      return next;
    });
  };

  const downloadUrl = (path, inline) =>
    `${API}/network-files/download?path=${encodeURIComponent(path)}&inline=${inline ? 'true' : 'false'}`;
  const openUrl = (path) => `${API}/network-files/preview?path=${encodeURIComponent(path)}`;
  const openFile = (path) => window.open(openUrl(path), '_blank', 'noopener,noreferrer');

  const openSelection = () => {
    if (!selected || selected.entry.is_dir) {
      toast.error(t('filesSelectFileForAction'));
      return;
    }
    openFile(selected.path);
  };

  const downloadSelection = () => {
    if (!selected || selected.entry.is_dir) {
      toast.error(t('filesSelectFileForAction'));
      return;
    }
    window.open(downloadUrl(selected.path, false), '_blank', 'noopener,noreferrer');
  };

  const rows = useMemo(() => {
    const out = [];
    function emit(path, lineagePrefix) {
      const entries = childrenByPath.get(path);
      if (entries === undefined) {
        if (loadingPaths.has(path)) {
          out.push({ kind: 'loading', key: `loading-${path}`, lineage: [...lineagePrefix, false] });
        }
        return;
      }
      entries.forEach((entry, idx) => {
        const hasMoreBelow = idx < entries.length - 1;
        const lineage = [...lineagePrefix, hasMoreBelow];
        const fullPath = path ? `${path}/${entry.name}` : entry.name;
        if (entry.is_dir) {
          const isOpen = expanded.has(fullPath);
          out.push({ kind: 'folder', entry, path: fullPath, lineage, isOpen });
          if (isOpen) emit(fullPath, lineage);
        } else {
          out.push({ kind: 'file', entry, path: fullPath, lineage });
        }
      });
    }
    const rootEntries = childrenByPath.get('') || [];
    rootEntries.forEach((entry, idx) => {
      const hasNextRoot = idx < rootEntries.length - 1;
      const fullPath = entry.name;
      if (entry.is_dir) {
        const isOpen = expanded.has(fullPath);
        out.push({ kind: 'folder', entry, path: fullPath, lineage: [], isOpen });
        if (isOpen) emit(fullPath, [hasNextRoot]);
      } else {
        out.push({ kind: 'file', entry, path: fullPath, lineage: [] });
      }
    });
    return out;
  }, [childrenByPath, expanded, loadingPaths]);

  const tbIcon =
    'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[#e2e8f0] bg-white text-[#334155] shadow-sm transition hover:border-[#cbd5e1] hover:bg-[#f8fafc] disabled:pointer-events-none disabled:opacity-35 dark:border-[#334155] dark:bg-[#1e293b] dark:text-[#e2e8f0] dark:hover:bg-[#334155]';
  const tbText =
    'inline-flex h-9 min-w-[4.75rem] shrink-0 items-center justify-center rounded-md border border-[#e2e8f0] bg-white px-2 text-xs font-semibold text-[#334155] shadow-sm transition hover:border-[#cbd5e1] hover:bg-[#f8fafc] disabled:pointer-events-none disabled:opacity-35 dark:border-[#334155] dark:bg-[#1e293b] dark:text-[#e2e8f0] dark:hover:bg-[#334155]';

  const shareLabel = status?.label || t('filesNetworkLabel');
  const syncInfo = status?.sync;
  const unavailable = !initialLoading && status && !status.available && !syncInfo?.has_content && !showBrowserSync;
  const isSyncedCopy = status?.source === 'sync' && status?.available;

  const lastSyncLabel = useMemo(() => {
    const raw = syncInfo?.last_sync_at;
    if (!raw) return null;
    try {
      return new Date(raw).toLocaleString();
    } catch {
      return null;
    }
  }, [syncInfo?.last_sync_at]);

  const syncProgressLabel = useMemo(() => {
    if (!syncProgress) return null;
    if (syncProgress.phase === 'manifest') return t('filesNetworkSyncChecking');
    if (syncProgress.phase === 'folder') {
      return t('filesNetworkSyncFolder').replace('{name}', syncProgress.folder || '');
    }
    if (syncProgress.phase === 'file') {
      return t('filesNetworkSyncFile')
        .replace('{folder}', syncProgress.folder || '')
        .replace('{current}', String(syncProgress.current || 0))
        .replace('{total}', String(syncProgress.total || 0));
    }
    return null;
  }, [syncProgress, t]);

  return (
    <section className="vmk-card-flat p-4">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between mb-3">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-[#94a3b8]">{t('filesNetworkLabel')}</h4>
        <p className="flex items-center gap-1.5 text-sm text-[#64748b] truncate max-w-xl">
          <HardDrives size={16} weight="duotone" className="shrink-0 text-emerald-600" />
          <span className="truncate">{localFolderName || shareLabel}</span>
          {isSyncedCopy ? (
            <span className="shrink-0 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
              {t('filesNetworkSyncedBadge')}
            </span>
          ) : null}
        </p>
      </div>

      {lastSyncLabel ? (
        <p className="mb-2 text-xs text-[#94a3b8]">
          {t('filesNetworkLastSync')}: {lastSyncLabel}
          {syncInfo?.last_agent ? ` (${syncInfo.last_agent})` : ''}
        </p>
      ) : null}

      {hasLiveShare ? (
        <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900 dark:bg-emerald-950/40">
          <p className="text-sm text-emerald-900 dark:text-emerald-200">{t('filesNetworkLiveConnected')}</p>
        </div>
      ) : null}

      {showBrowserSync ? (
        <div className="mb-3 rounded-lg border border-[#e2e8f0] bg-[#f8fafc] p-3 dark:border-[#334155] dark:bg-[#0f172a]">
          <p className="text-sm text-[#475569] dark:text-[#cbd5e1]">{t('filesNetworkBrowserSyncHint')}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={`${tbText} !min-w-0 gap-1.5 px-3`}
              onClick={chooseFolder}
              disabled={syncing || !browserSyncSupported}
            >
              <FolderOpen size={18} weight="duotone" className="text-emerald-600" />
              {localFolderName ? t('filesNetworkChangeFolder') : t('filesNetworkChooseFolder')}
            </button>
            <button
              type="button"
              className={`${tbText} !min-w-0 gap-1.5 px-3 bg-emerald-600 !text-white border-emerald-600 hover:bg-emerald-700`}
              onClick={syncNow}
              disabled={syncing || !browserSyncSupported}
            >
              <CloudArrowUp size={18} weight="bold" />
              {syncing ? t('filesNetworkSyncing') : t('filesNetworkSyncNow')}
            </button>
            <label className="ml-auto flex cursor-pointer items-center gap-2 text-xs text-[#64748b]">
              <input
                type="checkbox"
                checked={autoSync}
                onChange={(e) => {
                  setAutoSync(e.target.checked);
                  setAutoSyncPreference(e.target.checked);
                }}
                className="rounded border-[#cbd5e1]"
              />
              {t('filesNetworkAutoSync')}
            </label>
          </div>
          {syncProgressLabel ? <p className="mt-2 text-xs text-emerald-700 dark:text-emerald-400">{syncProgressLabel}</p> : null}
          {!browserSyncSupported ? (
            <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">{t('filesNetworkUnsupportedBrowser')}</p>
          ) : null}
        </div>
      ) : null}

      <div className="mb-3 flex flex-wrap items-center gap-1.5 rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-2 py-1.5 dark:border-[#334155] dark:bg-[#0f172a]">
        <button type="button" className={tbIcon} title={t('filesRefresh')} onClick={refresh} disabled={syncing}>
          <ArrowsClockwise size={20} weight="duotone" className="text-emerald-600" />
        </button>
        <button
          type="button"
          className={tbIcon}
          title={t('filesDownload')}
          onClick={downloadSelection}
          disabled={!selected || selected.entry.is_dir}
        >
          <DownloadSimple size={20} weight="duotone" />
        </button>
        <button
          type="button"
          className={`${tbText} ml-auto`}
          title={t('filesOpen')}
          onClick={openSelection}
          disabled={!selected || selected.entry.is_dir}
        >
          {t('filesOpen')}
        </button>
      </div>

      {initialLoading ? (
        <div className="flex justify-center py-12 text-sm text-[#64748b]">{t('filesLoading')}</div>
      ) : unavailable ? (
        <div className="rounded-lg border border-dashed border-[#cbd5e1] bg-[#f8fafc] p-4 dark:border-[#334155] dark:bg-[#0f172a]">
          <p className="text-sm text-[#64748b]">
            {status.configured ? t('filesNetworkUnavailable') : t('filesNetworkNotConfigured')}
          </p>
        </div>
      ) : (
        <div
          className="order-files-tree-panel max-h-[calc(100vh-330px)] min-h-[200px] overflow-y-auto rounded-lg border border-[#cfd8e3] bg-[#fafbfc] font-sans text-[13px] leading-snug text-[#1e293b] shadow-inner select-none dark:border-[#334155] dark:bg-[#0f172a] dark:text-[#e2e8f0]"
          role="tree"
        >
          {rows.length === 0 ? (
            <p className="p-4 text-[#64748b]">{showBrowserSync && rows.length === 0 ? t('filesNetworkEmptyChooseFolder') : t('filesNetworkEmpty')}</p>
          ) : (
            <ul className="order-files-tree-list py-0.5">
              {rows.map((row) => {
                if (row.kind === 'loading') {
                  return (
                    <li key={row.key} className="flex min-h-[30px] items-stretch pr-2">
                      <TreeGuideGutter lineage={row.lineage} />
                      <span className="flex items-center py-0.5 pl-1 text-xs text-[#94a3b8]">{t('filesLoading')}</span>
                    </li>
                  );
                }
                const sel = selected?.path === row.path;
                const rowCls = `order-files-tree-row group flex min-h-[30px] items-stretch pr-2 transition-colors ${
                  sel ? 'order-files-tree-row-selected bg-[#dbeafe] dark:bg-blue-950/50' : 'hover:bg-white/90 dark:hover:bg-slate-800/50'
                }`;
                if (row.kind === 'folder') {
                  const { entry, path, isOpen, lineage } = row;
                  return (
                    <li key={`d-${path}`} className={rowCls}>
                      <TreeGuideGutter lineage={lineage} />
                      <div className="flex min-w-0 flex-1 items-center gap-0.5 py-0.5">
                        <button
                          type="button"
                          className="flex h-8 w-7 shrink-0 items-center justify-center rounded text-[#475569] hover:bg-black/[0.04] dark:hover:bg-white/10"
                          aria-expanded={isOpen}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpand(path);
                          }}
                        >
                          {isOpen ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
                        </button>
                        <button
                          type="button"
                          className="flex min-w-0 flex-1 items-center gap-2 rounded py-0.5 text-left"
                          onClick={() => setSelected({ path, entry })}
                          onDoubleClick={() => toggleExpand(path)}
                        >
                          <IconFolderClosed
                            uid={`nf-${path.replace(/[^a-zA-Z0-9_-]/g, '_')}`}
                            size={20}
                            className="shrink-0 drop-shadow-sm"
                          />
                          <span className="truncate font-medium text-[#0f172a] dark:text-[#f1f5f9]">{entry.name}</span>
                        </button>
                      </div>
                    </li>
                  );
                }
                const { entry, path, lineage } = row;
                return (
                  <li
                    key={`f-${path}`}
                    className={`${rowCls} cursor-grab active:cursor-grabbing`}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'copy';
                      e.dataTransfer.setData('application/x-vmk-network-file', path);
                      e.dataTransfer.setData('text/plain', entry.name);
                      setSelected({ path, entry });
                    }}
                  >
                    <TreeGuideGutter lineage={lineage} />
                    <div className="flex min-w-0 flex-1 items-center gap-2 py-0.5 pl-1">
                      <button
                        type="button"
                        className="flex min-w-0 flex-1 items-center gap-2 rounded py-0.5 text-left"
                        onClick={() => setSelected({ path, entry })}
                        onDoubleClick={() => openFile(path)}
                      >
                        <TreeFileGlyph kind={fileKind(entry.name, '')} size={20} />
                        <span className="truncate text-[#334155] dark:text-[#cbd5e1]">{entry.name}</span>
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {!initialLoading && !unavailable ? (
        <p className="mt-2 text-xs text-[#94a3b8]">{t('filesNetworkDragHint')}</p>
      ) : null}
    </section>
  );
}
